import React from 'react';
import styled, { keyframes } from 'styled-components';
import { useLanguage } from '../context/LanguageContext';

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const float = keyframes`
  0% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-15px);
  }
  100% {
    transform: translateY(0px);
  }
`;

const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(54, 162, 235, 0.4);
  }
  70% {
    box-shadow: 0 0 0 20px rgba(54, 162, 235, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(54, 162, 235, 0);
  }
`;

// Styled components
const CTASection = styled.section`
  padding: 6rem 2rem;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary} 0%, ${props => props.theme.colors.primaryDark} 100%);
  color: white;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle at 70% 30%, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
    z-index: 1;
  }
`;

const SectionInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

const CTAContent = styled.div`
  animation: ${fadeIn} 0.8s ease forwards;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    order: 2;
  }
`;

const CTATitle = styled.h2`
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  line-height: 1.2;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 2.25rem;
  }
`;

const CTADescription = styled.p`
  font-size: 1.25rem;
  margin-bottom: 2.5rem;
  opacity: 0.9;
  line-height: 1.6;
  max-width: 600px;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    margin-left: auto;
    margin-right: auto;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1.1rem;
  }
`;

const AppStoreButtons = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    justify-content: center;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    flex-direction: column;
    align-items: center;
  }
`;

const StoreButton = styled.a`
  display: flex;
  align-items: center;
  background-color: ${props => props.light ? 'white' : 'black'};
  color: ${props => props.light ? 'black' : 'white'};
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    width: 80%;
    justify-content: center;
  }
`;

const StoreIcon = styled.div`
  font-size: 2rem;
  margin-right: 0.75rem;
`;

const StoreText = styled.div`
  display: flex;
  flex-direction: column;
`;

const StoreTagline = styled.span`
  font-size: 0.7rem;
  margin-bottom: 0.25rem;
`;

const StoreName = styled.span`
  font-size: 1.1rem;
  font-weight: 600;
`;

const CTAFeatures = styled.div`
  margin-top: 3rem;
`;

const FeaturesList = styled.ul`
  list-style: none;
  padding: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: center;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    justify-content: center;
  }
`;

const FeatureIcon = styled.div`
  margin-right: 0.75rem;
  font-size: 1.25rem;
`;

const FeatureText = styled.span`
  font-size: 1rem;
`;

const CTAVisual = styled.div`
  position: relative;
  height: 500px;
  animation: ${fadeIn} 0.8s ease forwards;
  animation-delay: 0.3s;
  opacity: 0;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    order: 1;
    height: 400px;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    height: 350px;
  }
`;

const PhoneFrame = styled.div`
  position: absolute;
  width: 270px;
  height: 550px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: white;
  border-radius: 36px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  z-index: 2;
  animation: ${float} 6s ease-in-out infinite;
  
  &::before {
    content: '';
    position: absolute;
    top: 12px;
    left: 50%;
    transform: translateX(-50%);
    width: 150px;
    height: 25px;
    background-color: black;
    border-radius: 20px;
    z-index: 3;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 15px;
    left: 50%;
    transform: translateX(-50%);
    width: 120px;
    height: 5px;
    background-color: #ddd;
    border-radius: 20px;
    z-index: 3;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    width: 230px;
    height: 470px;
  }
`;

const PhoneScreen = styled.div`
  position: absolute;
  top: 25px;
  left: 10px;
  right: 10px;
  bottom: 25px;
  background-color: #f5f5f5;
  border-radius: 26px;
  overflow: hidden;
`;

const PhoneContent = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  background: linear-gradient(to bottom, #e0f2ff 0%, #ffffff 100%);
`;

const PhoneHeader = styled.div`
  padding: 15px;
  text-align: center;
  background-color: ${props => props.theme.colors.primary};
  color: white;
`;

const PhoneTitle = styled.div`
  font-weight: 600;
  font-size: 0.9rem;
`;

const PhoneMap = styled.div`
  position: relative;
  width: 100%;
  height: 70%;
  background-color: #e9f5ff;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: 
      linear-gradient(90deg, rgba(0,0,0,0.03) 1px, transparent 1px),
      linear-gradient(rgba(0,0,0,0.03) 1px, transparent 1px);
    background-size: 20px 20px;
  }
`;

const PhoneWeatherIcon = styled.div`
  position: absolute;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
  z-index: 2;
  animation: ${pulse} 2s infinite;
  
  &:nth-child(1) {
    top: 30%;
    left: 40%;
  }
  
  &:nth-child(2) {
    top: 50%;
    left: 60%;
    animation-delay: 0.7s;
  }
  
  &:nth-child(3) {
    top: 20%;
    left: 70%;
    animation-delay: 1.4s;
  }
`;

const PhoneControls = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 10px;
  background-color: white;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: space-around;
`;

const PhoneButton = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${props => props.primary ? props.theme.colors.primary : '#f5f5f5'};
  color: ${props => props.primary ? 'white' : '#666'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
`;

const SecondPhone = styled.div`
  position: absolute;
  width: 270px;
  height: 550px;
  top: 60%;
  left: 35%;
  transform: translate(-50%, -50%) rotate(-10deg);
  background-color: white;
  border-radius: 36px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  z-index: 1;
  opacity: 0.7;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    display: none;
  }
`;

const DownloadLink = styled.a`
  display: inline-block;
  background-color: white;
  color: ${props => props.theme.colors.primary};
  font-weight: 600;
  padding: 1rem 2.5rem;
  border-radius: 50px;
  font-size: 1.1rem;
  margin-top: 1rem;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 1rem 2rem;
    font-size: 1rem;
  }
`;

// CallToAction component
const CallToAction = () => {
  const { translate } = useLanguage();
  
  return (
    <CTASection id="download">
      <SectionInner>
        <Grid>
          <CTAContent>
            <CTATitle>
              {translate('cta.title', 'Experience Real-Time Weather Reports')}
            </CTATitle>
            <CTADescription>
              {translate('cta.description', 'Download WeatherTeller today and join our global community of weather enthusiasts. Share reports, discover local conditions, and make more informed decisions.')}
            </CTADescription>
            
            <AppStoreButtons>
              <StoreButton href="#" light={false}>
                <StoreIcon>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12.1532 7.30545L12.3155 7.00285C12.6308 6.39132 13.1343 5.87966 13.7548 5.53497C14.3752 5.19028 15.0839 5.02741 15.7909 5.06706C16.1619 5.0958 16.5259 5.17454 16.8725 5.30086L17.6878 5.59316C17.9961 5.70674 18.3201 5.77434 18.6478 5.79394L18.7414 5.79894C19.7403 5.85823 20.7058 5.57809 21.4988 5.00364C21.1778 6.00709 20.5617 6.8981 19.7225 7.56325C19.5593 7.69112 19.3814 7.79709 19.1925 7.87883C18.7519 8.07791 18.3752 8.39777 18.09 8.8044C17.9336 9.07015 17.8242 9.36065 17.7659 9.66244L17.7365 9.81305C17.6705 10.1549 17.6373 10.5032 17.637 10.8524V11.7025C17.637 12.9034 17.3731 14.0918 16.8609 15.1951L16.8079 15.3003C16.0266 16.9099 15.0386 18.4122 13.8664 19.7676L13.698 19.9619C13.5705 20.1122 13.3814 20.1984 13.1835 20.1984H10.5973C10.3994 20.1984 10.2103 20.1122 10.0828 19.9619L9.91442 19.7676C8.74221 18.4122 7.75419 16.9099 6.97285 15.3003L6.91987 15.1951C6.4077 14.0918 6.14376 12.9034 6.14376 11.7025V10.8524C6.14346 10.5032 6.11026 10.1549 6.04425 9.81305L6.01479 9.66244C5.95654 9.36065 5.84711 9.07015 5.69072 8.8044C5.40553 8.39777 5.02883 8.07791 4.58825 7.87883C4.39938 7.79709 4.22146 7.69112 4.05825 7.56325C3.21901 6.89814 2.60289 6.00715 2.28193 5.00364C3.0749 5.57809 4.04044 5.85823 5.03939 5.79894L5.13297 5.79394C5.46063 5.77434 5.78465 5.70674 6.09297 5.59316L6.90826 5.30086C7.25479 5.17454 7.61883 5.0958 7.98989 5.06706C8.69688 5.02741 9.40553 5.19028 10.026 5.53497C10.6464 5.87966 11.1499 6.39132 11.4653 7.00285L11.6275 7.30545C11.7255 7.50325 12.0552 7.50325 12.1532 7.30545Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </StoreIcon>
                <StoreText>
                  <StoreTagline>{translate('cta.appStore.get', 'GET IT ON')}</StoreTagline>
                  <StoreName>{translate('cta.appStore.name', 'App Store')}</StoreName>
                </StoreText>
              </StoreButton>
              
              <StoreButton href="#" light={false}>
                <StoreIcon>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3.5 20.5L12 3.5L20.5 20.5H3.5Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M12 8.5L5.5 20.5H18.5L12 8.5Z" fill="white" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </StoreIcon>
                <StoreText>
                  <StoreTagline>{translate('cta.googlePlay.get', 'GET IT ON')}</StoreTagline>
                  <StoreName>{translate('cta.googlePlay.name', 'Google Play')}</StoreName>
                </StoreText>
              </StoreButton>
            </AppStoreButtons>
            
            <DownloadLink href="#">
              {translate('cta.webVersion', 'Try Web Version')}
            </DownloadLink>
            
            <CTAFeatures>
              <FeaturesList>
                <FeatureItem>
                  <FeatureIcon>✓</FeatureIcon>
                  <FeatureText>{translate('cta.features.free', 'Free to use')}</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon>✓</FeatureIcon>
                  <FeatureText>{translate('cta.features.offline', 'Works offline')}</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon>✓</FeatureIcon>
                  <FeatureText>{translate('cta.features.updates', 'Regular updates')}</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon>✓</FeatureIcon>
                  <FeatureText>{translate('cta.features.noAds', 'No annoying ads')}</FeatureText>
                </FeatureItem>
              </FeaturesList>
            </CTAFeatures>
          </CTAContent>
          
          <CTAVisual>
            <PhoneFrame>
              <PhoneScreen>
                <PhoneContent>
                  <PhoneHeader>
                    <PhoneTitle>WeatherTeller</PhoneTitle>
                  </PhoneHeader>
                  <PhoneMap>
                    <PhoneWeatherIcon>☀️</PhoneWeatherIcon>
                    <PhoneWeatherIcon>🌧️</PhoneWeatherIcon>
                    <PhoneWeatherIcon>⛅</PhoneWeatherIcon>
                  </PhoneMap>
                  <PhoneControls>
                    <PhoneButton>🗺️</PhoneButton>
                    <PhoneButton primary>➕</PhoneButton>
                    <PhoneButton>👤</PhoneButton>
                  </PhoneControls>
                </PhoneContent>
              </PhoneScreen>
            </PhoneFrame>
            
            <SecondPhone />
          </CTAVisual>
        </Grid>
      </SectionInner>
    </CTASection>
  );
};

export default CallToAction;